/* eslint-disable no-magic-numbers -- for now */
/* eslint-disable promise/avoid-new -- for now */
// @/services/helia/core/dataUtils.ts
import { concat as uint8ArrayConcat } from 'uint8arrays/concat';
import { toString as uint8ArrayToString, type SupportedEncodings } from 'uint8arrays/to-string';

import type { ProgressCallback } from '@/types/poc/helia';
import { createLogger, type Logger } from '@/utils/logger';

const log: Logger = createLogger({ namespace: 'six3ro:utils:data' });

/**
 * Convert a Uint8Array to a Blob with the specified MIME type
 *
 * @param data - The Uint8Array data to convert
 * @param mimeType - The MIME type for the Blob (default: application/octet-stream)
 * @returns A Blob containing the data
 */
export const uint8ArrayToBlob = (
  data: Uint8Array,
  mimeType = 'application/octet-stream'
): Blob => {
  // Ensure we have an ArrayBuffer-backed Uint8Array
  const arrayBuffer = new ArrayBuffer(data.length);
  const uint8Array = new Uint8Array(arrayBuffer);
  uint8Array.set(data);

  return new Blob([uint8Array], { type: mimeType });
};/**
 * Convert a Uint8Array to a string
 *
 * @param data - The Uint8Array data to convert
 * @param encoding - The encoding to use (default: utf-8)
 * @returns The string representation of the data
 */
export const uint8ArrayToText = (
  data: Uint8Array,
  encoding: SupportedEncodings = 'utf-8'
): string => uint8ArrayToString(data, encoding);

/**
 * Convert a File to an async iterable for processing file data in chunks
 *
 * @param file - The file to convert
 * @param onProgress - Optional progress callback
 * @returns AsyncIterable of Uint8Array chunks
 */
export async function* fileToAsyncIterable(
  file: File,
  onProgress?: ProgressCallback
): AsyncIterable<Uint8Array> {
  let bytesRead = 0;
  const chunkSize = 64 * 1024; // 64KB chunks - smaller for better flow control over relay
  const totalChunks = Math.ceil(file.size / chunkSize);
  let chunkCount = 0;

  for (let position = 0; position < file.size; position += chunkSize) {
    chunkCount++;
    const blob = file.slice(position, position + chunkSize);
    const arrayBuffer = await readBlobAsArrayBuffer(blob);

    bytesRead += blob.size;

    if (onProgress !== undefined) {
      onProgress(bytesRead, file.size);
    }

    yield new Uint8Array(arrayBuffer);

    // Log progress every 16 chunks (~1MB)
    if (chunkCount % 16 === 0) {
      const progressPct = ((bytesRead / file.size) * 100).toFixed(1);
      log.debug(`Upload progress: ${progressPct}% (${chunkCount}/${totalChunks} chunks)`);
    }

    // Add small delay every 16 chunks (~1MB) to allow network breathing room
    if (position > 0 && (position / chunkSize) % 16 === 0) {
      await new Promise(resolve => { setTimeout(resolve, 10); });
    }
  }
}

/**
 * Read a Blob as an ArrayBuffer
 *
 * @param blob - The Blob to read
 * @returns Promise resolving to an ArrayBuffer
 */
export const readBlobAsArrayBuffer = async(blob: Blob): Promise<ArrayBuffer> => await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not an ArrayBuffer'));
      }
    };

    reader.onerror = () => { reject(new Error(reader.error?.message ?? 'FileReader error')); };
    reader.readAsArrayBuffer(blob);
  });

/**
 * Concatenate an array of Uint8Arrays into a single Uint8Array
 *
 * @param chunks - Array of Uint8Array chunks
 * @returns A single concatenated Uint8Array
 */
export const concatUint8Arrays = (chunks: Uint8Array[]): Uint8Array => uint8ArrayConcat(chunks);

/**
 * Create a download link and trigger a file download
 *
 * @param blob - The Blob to download
 * @param filename - The filename to use
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;

  // Trigger download
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Clean up
  URL.revokeObjectURL(url);
};
